import {
  Button,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  createExitHold,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { DeviceFiles } from '@/features/devices/DeviceFiles';
import { DeviceImage } from '@/features/devices/DeviceImage';
import { Lightbox } from '@/features/devices/Lightbox';
import { useAssignDevice, useMyDevices, useUpdateDevice } from '@/features/devices/hooks';
import { cn } from '@/lib/cn';
import type { DeviceFile, DeviceFileGroup, MyDevice } from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

// UCH-437: return-device "Thanos snap" — dust burst + dissolve via CSS keyframes
// (index.css device-*), randomized inline styles like WelcomeCelebration.
const SNAP_MS = 700;
const between = (min: number, max: number) => min + Math.random() * (max - min);
const makeDust = () =>
  Array.from({ length: 40 }, () => ({
    left: between(2, 98),
    top: between(5, 95),
    size: between(3, 6),
    dx: between(20, 90),
    dy: between(-70, -15),
    delay: between(0, 200),
  }));

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

// Expand/collapse via the CSS grid-rows trick so height animates without JS
// measuring; margin transitions along to cancel the parent's gap-4 slot, and
// `starting:` plays the same transition on mount (paired with createExitHold
// so the exit plays before unmount).
const collapseClass = (open: boolean) =>
  cn(
    'grid transition-[grid-template-rows,opacity,margin] duration-200 ease-out motion-reduce:transition-none',
    open
      ? 'grid-rows-[1fr] opacity-100 starting:-mt-4 starting:grid-rows-[0fr] starting:opacity-0'
      : '-mt-4 grid-rows-[0fr] opacity-0',
  );

export default function MyDevicePage() {
  const query = useMyDevices();

  return (
    <section>
      <PageHeader title="My devices" />
      <div class="mx-auto max-w-6xl p-6">
        <Show when={!query.isPending} fallback={<Skeleton class="h-40" />}>
          <Show
            when={query.data}
            fallback={<EmptyState variant="error" title="Failed to load your devices" />}
          >
            <Show
              when={(query.data?.data.length ?? 0) > 0}
              fallback={
                <EmptyState
                  title="No device assigned to you"
                  body="When a device is assigned to you, it will show up here."
                />
              }
            >
              <div class="flex flex-col gap-6">
                <For each={query.data?.data ?? []}>
                  {(device) => (
                    <MyDeviceCard device={device} fileGroups={query.data?.meta.fileGroups ?? []} />
                  )}
                </For>
              </div>
            </Show>
          </Show>
        </Show>
      </div>
    </section>
  );
}

const MyDeviceCard = (props: { device: MyDevice; fileGroups: DeviceFileGroup[] }) => {
  const { session } = useAuth();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const update = useUpdateDevice(props.device.id);
  const assign = useAssignDevice(props.device.id);
  const [notes, setNotes] = createSignal(props.device.notes);
  const [editing, setEditing] = createSignal(false);
  // Keeps the edit-only regions mounted through their 200ms slide-shut.
  const editingHeld = createExitHold(() => editing() || null);
  const [removing, setRemoving] = createSignal(false);

  const photos = () =>
    props.device.files
      .filter((f) => f.groupSlug === 'photos' && f.contentType.startsWith('image/'))
      .sort((a, b) => a.sortOrder - b.sortOrder);

  const [lightbox, setLightbox] = createSignal<DeviceFile | null>(null);

  const canEditNotes = () =>
    (session()?.permissions.has('devices:editOwnNote') ?? false) ||
    (session()?.permissions.has('devices:edit') ?? false);

  const saveNotes = async () => {
    try {
      await update.mutateAsync({ notes: notes() });
      setEditing(false);
      toast.success('Note updated');
    } catch {
      toast.error('Update failed');
    }
  };

  const returnDevice = async () => {
    if (!(await confirmDialog({ title: `Return ${props.device.name}?`, confirmLabel: 'Return' })))
      return;
    try {
      if (!prefersReducedMotion()) {
        // ponytail: animate-then-mutate; parallelize if the 700ms pre-request latency ever matters
        setRemoving(true);
        await new Promise((resolve) => setTimeout(resolve, SNAP_MS));
      }
      await assign.mutateAsync({ userId: null });
      toast.success('Device returned');
    } catch {
      setRemoving(false);
      toast.error('Return failed');
    }
  };

  return (
    // Collapse-to-zero wrapper: the snap dissolve plays first, then the row
    // height eases shut so the cards below slide up before the refetch unmounts us.
    <div
      inert={removing()}
      class={cn(
        'grid transition-[grid-template-rows] delay-200 duration-300 ease-out motion-reduce:transition-none',
        removing() ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]',
      )}
    >
      <div class={cn(removing() && 'overflow-hidden')}>
        <div
          class={cn(
            'relative flex gap-4 rounded-xl border border-neutral-100 bg-surface p-4 shadow-sm dark:border-neutral-800 dark:bg-surface-dark',
            removing() && 'device-snap',
          )}
        >
          <Show when={!editing() ? photos()[0] : undefined}>
            {(cover) => (
              <button
                type="button"
                class="w-40 shrink-0 self-start"
                onClick={() => setLightbox(cover())}
                aria-label={cover().filename}
              >
                <DeviceImage
                  src={cover().url}
                  alt={cover().filename}
                  class="aspect-square w-full rounded-lg"
                />
              </button>
            )}
          </Show>
          <div class="flex min-w-0 flex-1 flex-col gap-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold">{props.device.name}</h2>
                <p class="text-sm text-neutral-500">
                  {[
                    props.device.model,
                    props.device.os === 'ios'
                      ? 'iOS'
                      : props.device.os === 'android'
                        ? 'Android'
                        : 'Other',
                    props.device.serial && `SN ${props.device.serial}`,
                    props.device.imei && `IMEI ${props.device.imei}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setEditing(!editing())}>
                  {editing() ? 'Done' : 'Edit'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={returnDevice}
                  loading={assign.isPending || removing()}
                >
                  Return device
                </Button>
              </div>
            </div>

            <Show when={!editing() && props.device.notes}>
              <p class="text-sm text-neutral-600 dark:text-neutral-400">{props.device.notes}</p>
            </Show>

            <Show when={editingHeld() && canEditNotes()}>
              <div inert={!editing()} class={collapseClass(editing())}>
                {/* p/-m slack keeps the input's focus ring clear of the overflow clip */}
                <div class="-m-1 overflow-hidden p-1">
                  <div class="flex items-end gap-2">
                    <div class="flex-1">
                      <Input
                        label="Notes"
                        value={notes()}
                        onInput={(e) => setNotes(e.currentTarget.value)}
                      />
                    </div>
                    <Button onClick={saveNotes} loading={update.isPending}>
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </Show>

            <Show when={!editing() && photos().length > 1}>
              <div class="flex flex-wrap gap-2">
                <For each={photos().slice(1)}>
                  {(photo) => (
                    <button
                      type="button"
                      onClick={() => setLightbox(photo)}
                      aria-label={photo.filename}
                    >
                      <DeviceImage
                        src={photo.url}
                        alt={photo.filename}
                        class="h-16 w-16 rounded-lg"
                      />
                    </button>
                  )}
                </For>
              </div>
            </Show>

            <Show when={editingHeld()}>
              <div inert={!editing()} class={collapseClass(editing())}>
                <div class="-m-1 overflow-hidden p-1">
                  <DeviceFiles
                    deviceId={props.device.id}
                    files={props.device.files}
                    fileGroups={props.fileGroups}
                    assignedUserId={props.device.assignedUserId}
                  />
                </div>
              </div>
            </Show>
          </div>

          <Show when={removing()}>
            <div
              class="pointer-events-none absolute inset-0 text-neutral-400 dark:text-neutral-500"
              aria-hidden="true"
            >
              <For each={makeDust()}>
                {(d) => (
                  <span
                    class="device-dust"
                    style={`left:${d.left}%;top:${d.top}%;width:${d.size}px;height:${d.size}px;--dx:${d.dx}px;--dy:${d.dy}px;animation-delay:${d.delay}ms`}
                  />
                )}
              </For>
            </div>
          </Show>

          <Lightbox file={lightbox()} onClose={() => setLightbox(null)} />
        </div>
      </div>
    </div>
  );
};
